import {
  createSnowplowTracker,
  type IgluSchemaUri,
} from '@gympass/event-tracker-snowplow';

import config from 'config';

// `com.wellhub.partners/self_sign_up` was never registered in
// gympass/opensnowcat-iglu-schemas (confirmed 2026-09-29, not just an
// out-of-date version — the vendor namespace doesn't exist at all), so events
// were validating against a schema that can't resolve. Standing in with the
// permissive, already-registered `com.gympass/click_stream/1-0-0`
// (additionalProperties: true, no declared fields) for both event kinds:
// it can't reject anything, so it can't silently dead-letter events the way
// a strict but wrong schema would (see ADR 023's June 2025 incident note).
// Neither key is a real conformance target — `'page-view'` isn't even called
// anywhere today (every event, page view included, goes through
// `eventTracker.record('action', ...)`, see EventTrackingProvider.tsx) — so
// treat this as "stop dropping events," not "correctly schema'd." Real,
// dedicated schemas belong in gympass/opensnowcat-iglu-schemas once the
// event shape is conformed with the data team; until then this mapping stays
// a placeholder.
//
// `IgluSchemaUri` only accepts the newer `com.wellhub.<focus_area>`
// vendor convention (e.g. `com.wellhub.code/sign_up`); `com.gympass` predates
// that convention but is still a real, resolvable vendor in the same
// registry (confirmed 2026-09-29 alongside the schema itself), and no
// dotted-`com.wellhub.*` permissive equivalent exists to satisfy the type
// without reintroducing a strict-schema mismatch. Asserting past the type
// here is deliberate, not a type-safety gap to close.
const SCHEMA_URIS = {
  action: 'iglu:com.gympass/click_stream/jsonschema/1-0-0' as IgluSchemaUri,
  'page-view':
    'iglu:com.gympass/click_stream/jsonschema/1-0-0' as IgluSchemaUri,
} as const;

// Sends every event to openSnowcat only (Snowplow was removed in
// gympass/partners-acquisition#559 / ADR 023). The wrapper library keeps its
// Snowplow-protocol naming because openSnowcat speaks that protocol.
//
// The tracker doesn't use the wrapper library's own EventTrackerProvider /
// useEventTracker: EventTrackingProvider drives it directly (start, record,
// consent), so there's no consumer for the Provider/context pair it would
// otherwise return.
//
// Runtime Zod validation caused a production incident before (PAREXP-1270 /
// gympass/partners-self-signup-spa#866, reverted in #868) when it started
// throwing on schema mismatches. This app already has TypeScript; keep
// validation off rather than reintroduce that risk.
//
// encodeBase64 is deliberately true: the Data Platform team's own Tracking RFC
// requires it for self-describing/custom events sent over POST (it's off by
// default for POST), and this app's `action` event is exactly that case.
// Confirmed 2026-09-29 as the actual cause of openSnowcat events never
// reaching the datalake.
export const { tracker: eventTracker } = createSnowplowTracker({
  appId: config.APP_NAME,
  endpoint: config.OPENSNOWCAT_TRACKING_URL,
  trackerName: config.PARTNERS_ENVIRONMENT,
  encodeBase64: true,
  preservePageViewIdForUrl: 'pathname',
  validate: false,
  schemaUris: SCHEMA_URIS,
});
